import React, {useCallback, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  Container,
  IconButton,
  useMediaQuery,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import {BrandLogo, CloseModalIcon} from '@salesforce/retail-react-app/app/components/icons';
import {useBlurOverlay} from '@amblique/king-living/app/contexts/blur-overlay-context';
import Search from '@amblique/king-living/app/components/search';

/**
 * @override
 * - Add initialFocusRef to auto-focus search input when modal opens
 * - Use global blur overlay instead of ModalOverlay
 * - Pass inputRef to Search component for focus management
 * - On desktop: Keep header visible, position modal below header with dynamic height calculation
 */
const SearchModal = ({isOpen, onClose}) => {
  const styles = useMultiStyleConfig('SearchModal');
  const searchInputRef = useRef(null);
  const headerWrapperRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const rafIdRef = useRef(null);
  const resizeTimeoutRef = useRef(null);
  const [isMobile] = useMediaQuery('(max-width: 991px)');
  const [headerHeight, setHeaderHeight] = useState(0);
  const blurOverlay = useBlurOverlay();

  // Throttled header height calculation using requestAnimationFrame
  const calculateHeaderHeight = useCallback(() => {
    // Cancel any pending animation frame
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
    }

    rafIdRef.current = requestAnimationFrame(() => {
      const headerWrapper = headerWrapperRef.current || document.querySelector('[data-header-wrapper]');

      if (headerWrapper) {
        // Cache the element reference
        if (!headerWrapperRef.current) {
          headerWrapperRef.current = headerWrapper;
        }

        const height = headerWrapper.getBoundingClientRect().height;

        // Only update state if height actually changed to avoid unnecessary re-renders
        setHeaderHeight((prevHeight) => {
          if (Math.abs(prevHeight - height) > 0.5) {
            return height;
          }

          return prevHeight;
        });
      }
    });
  }, []);

  // Calculate header height dynamically on desktop
  useEffect(() => {
    if (isMobile || !isOpen) {
      // Reset height when modal closes or on mobile
      setHeaderHeight(0);

      return;
    }

    // Initial calculation
    calculateHeaderHeight();

    // Setup ResizeObserver with throttled callback
    if (!resizeObserverRef.current) {
      resizeObserverRef.current = new ResizeObserver(() => {
        calculateHeaderHeight();
      });
    }

    const headerWrapper = document.querySelector('[data-header-wrapper]');

    if (headerWrapper) {
      headerWrapperRef.current = headerWrapper;
      resizeObserverRef.current.observe(headerWrapper);
    }

    // Throttled window resize listener (ResizeObserver handles most cases, this is fallback)
    const handleResize = () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      resizeTimeoutRef.current = setTimeout(calculateHeaderHeight, 100);
    };

    window.addEventListener('resize', handleResize, {passive: true});

    return () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
      window.removeEventListener('resize', handleResize);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, [isMobile, isOpen, calculateHeaderHeight]);

  // Sync global blur overlay with modal state
  useEffect(() => {
    if (isOpen) {
      blurOverlay.show({
        onForceClose: onClose,
      });
    } else {
      blurOverlay.hide();
    }
  }, [isOpen, blurOverlay, onClose]);

  // Extract blockScrollOnMount from modal styles
  const {blockScrollOnMount, ...modalProps} = styles.modal || {};

  // Dynamic styles for content (not memoized)
  // On desktop: z-index 1490 (below header at 1500), on mobile: z-index 1500
  const contentStyles = isMobile ? styles.content : {...styles.content,
    marginTop: `${headerHeight}px`,
    containerProps: {
      ...styles.content?.containerProps,
      style: {
        ...styles.content?.containerProps?.style,
        zIndex: 1490, // Below header (1500) on desktop
      },
    },
  };

  return (
    <Modal
      blockScrollOnMount={blockScrollOnMount ?? false}
      data-testid="search-modal"
      initialFocusRef={searchInputRef}
      isOpen={isOpen}
      scrollBehavior="inside"
      size="full"
      {...modalProps}
      onClose={onClose}
    >
      <ModalContent
        className="search-modal-content"
        {...contentStyles}
      >
        {isMobile ? (
          <ModalHeader {...styles.mobileHeader}>
            <IconButton
              icon={<BrandLogo {...styles.logoMobile} />}
              variant="unstyled"
              onClick={onClose}
              {...styles.logoMobileWrapper}
            />
            <IconButton
              aria-label="Close search"
              icon={<CloseModalIcon h="18px" w="18px" />}
              variant="unstyled"
              onClick={onClose}
              {...styles.closeButton}
            />
          </ModalHeader>
        ) : (
          <IconButton
            aria-label="Close search"
            icon={<CloseModalIcon h="18px" w="18px" />}
            variant="ghost"
            onClick={onClose}
            {...styles.closeButton}
          />
        )}
        <ModalBody {...styles.body}>
          <Container {...styles.container}>
            <Search inputRef={searchInputRef} onCloseModal={onClose} />
          </Container>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

SearchModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default SearchModal;
